import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Users, Building2, Calendar, LogOut, Mail, Upload, CheckCircle, XCircle,
  Star, MessageSquare, Hotel, Send, Copy, Plus, Filter, Search, AlertTriangle
} from 'lucide-react';
import { api } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [giftOrders, setGiftOrders] = useState([]);
  const [customizedGiftOrders, setCustomizedGiftOrders] = useState([]);
  const [trainerBookings, setTrainerBookings] = useState([]);
  const [paymentTransactions, setPaymentTransactions] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [hotels5Star, setHotels5Star] = useState([]);
  const [hotelCities, setHotelCities] = useState([]);
  
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [selectedCity, setSelectedCity] = useState('');
  const [csvData, setCsvData] = useState('');
  
  // New states for enhanced functionality
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [rejectReason, setRejectReason] = useState('');
  const [userToReject, setUserToReject] = useState(null);
  const [isBulkReject, setIsBulkReject] = useState(false);
  
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      const [statsRes, usersRes, pendingRes, bookingsRes, giftOrdersRes, customGiftOrdersRes, trainerBookingsRes, paymentsRes, testimonialsRes, invitationsRes, hotelsRes, citiesRes] = await Promise.all([
        api.getAdminStats(),
        api.getAllUsers(),
        api.get('/admin/users/pending').catch(() => ({ data: [] })),
        api.getBookings(),
        api.getAdminGiftOrders().catch(() => ({ data: [] })),
        api.getAdminCustomizedGiftOrders().catch(() => ({ data: [] })),
        api.getAdminTrainerBookings().catch(() => ({ data: [] })),
        api.getAdminPaymentTransactions().catch(() => ({ data: [] })),
        api.getAdminTestimonials().catch(() => ({ data: [] })),
        api.getInvitations().catch(() => ({ data: [] })),
        api.get5StarHotels(),
        api.getHotelCities(),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setPendingUsers(pendingRes.data || []);
      setBookings(bookingsRes.data);
      setGiftOrders(giftOrdersRes.data || []);
      setCustomizedGiftOrders(customGiftOrdersRes.data || []);
      setTrainerBookings(trainerBookingsRes.data || []);
      setPaymentTransactions(paymentsRes.data || []);
      setTestimonials(testimonialsRes.data || []);
      setInvitations(invitationsRes.data || []);
      setHotels5Star(hotelsRes.data);
      setHotelCities(citiesRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    }
  };

  const handleTestimonialAction = async (id, status) => {
    try {
      await api.updateTestimonialStatus(id, status);
      toast.success(`Testimonial ${status}`);
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to update testimonial');
    }
  };

  const handleApproveUser = async (userId) => {
    try {
      const response = await api.post(`/admin/users/${userId}/approve`);
      toast.success(response.data.message || 'User approved successfully');
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve user');
    }
  };

  const handleRejectUser = async (userId, reason = 'Not specified') => {
    try {
      const response = await api.post(`/admin/users/${userId}/reject`, { reason });
      toast.success(response.data.message || 'User rejected');
      setShowRejectDialog(false);
      setRejectReason('');
      setUserToReject(null);
      setSelectedUsers([]);
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject user');
    }
  };

  // Open reject dialog with reason input
  const openRejectDialog = (userId) => {
    setUserToReject(userId);
    setIsBulkReject(false);
    setRejectReason('');
    setShowRejectDialog(true);
  };

  // Confirm rejection with reason
  const confirmReject = async () => {
    if (isBulkReject) {
      await handleBulkReject();
    } else if (userToReject) {
      await handleRejectUser(userToReject, rejectReason || 'Does not meet requirements');
    }
  };

  // Bulk approve selected users
  const handleBulkApprove = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select users to approve');
      return;
    }
    try {
      const response = await api.post('/admin/users/bulk-approve', { user_ids: selectedUsers });
      toast.success(response.data.message || `Approved ${response.data.approved_count} users`);
      setSelectedUsers([]);
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to bulk approve');
    }
  };

  // Bulk reject selected users
  const handleBulkReject = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select users to reject');
      return;
    }
    try {
      const response = await api.post('/admin/users/bulk-reject', { 
        user_ids: selectedUsers, 
        reason: rejectReason || 'Does not meet requirements' 
      });
      toast.success(response.data.message || `Rejected ${response.data.rejected_count} users`);
      setSelectedUsers([]);
      setShowRejectDialog(false);
      setRejectReason('');
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to bulk reject');
    }
  };

  // Open bulk reject dialog
  const openBulkRejectDialog = () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select users to reject');
      return;
    }
    setIsBulkReject(true);
    setRejectReason('');
    setShowRejectDialog(true);
  };

  // Toggle user selection
  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Select/Deselect all visible users
  const toggleSelectAll = () => {
    const visibleUsers = filteredPendingUsers.map(u => u.id);
    if (selectedUsers.length === visibleUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(visibleUsers);
    }
  };

  // Filter pending users based on search and role
  const filteredPendingUsers = pendingUsers.filter(u => {
    const matchesSearch = searchQuery === '' || 
      u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.company?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const handleInviteHotel = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    try {
      const res = await api.inviteHotel({
        hotel_name: formData.get('hotel_name'),
        hotel_city: formData.get('hotel_city'),
        contact_name: formData.get('contact_name'),
        contact_email: formData.get('contact_email'),
        contact_phone: formData.get('contact_phone'),
      });
      toast.success('Invitation created!');
      setShowInviteDialog(false);
      fetchAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invitation');
    }
  };

  const handleBulkUpload = async () => {
    try {
      // Parse CSV data
      const lines = csvData.trim().split('\n');
      const invitations = lines.slice(1).map(line => {
        const [hotel_name, hotel_city, contact_name, contact_email, contact_phone] = line.split(',').map(s => s.trim());
        return { hotel_name, hotel_city, contact_name, contact_email, contact_phone };
      }).filter(inv => inv.contact_email);
      
      if (invitations.length === 0) {
        toast.error('No valid data found in CSV');
        return;
      }
      
      await api.bulkInviteHotels({ invitations });
      toast.success(`${invitations.length} invitations created!`);
      setShowBulkDialog(false);
      setCsvData('');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to process bulk upload');
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setCsvData(event.target.result);
      };
      reader.readAsText(file);
    }
  };

  const copyInviteLink = (code) => {
    navigator.clipboard.writeText(`${window.location.origin}/register/vendor?code=${code}`);
    toast.success('Link copied to clipboard!');
  };

  const filteredHotels = selectedCity && selectedCity !== 'all'
    ? hotels5Star.filter(h => h.city === selectedCity)
    : hotels5Star;

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="bg-navy border-b border-navy-800 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <h1 className="font-heading text-2xl font-semibold text-white">Admin Dashboard</h1>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-300">Welcome, {user?.name}</span>
            <Button onClick={() => { logout(); navigate('/'); }} variant="outline" size="sm" className="border-white/30 text-white hover:bg-white/10">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg shadow-card border border-gray-100">
              <Users className="w-8 h-8 text-gold mb-4" />
              <p className="text-3xl font-heading font-semibold text-navy mb-1">{stats.total_users}</p>
              <p className="text-sm text-slate-700">Total Users</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-card border border-gray-100">
              <Building2 className="w-8 h-8 text-gold mb-4" />
              <p className="text-3xl font-heading font-semibold text-navy mb-1">{stats.total_properties}</p>
              <p className="text-sm text-slate-700">Total Properties</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-card border border-gray-100">
              <Calendar className="w-8 h-8 text-gold mb-4" />
              <p className="text-3xl font-heading font-semibold text-navy mb-1">{stats.total_bookings}</p>
              <p className="text-sm text-slate-700">Total Bookings</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-card border border-gray-100">
              <Mail className="w-8 h-8 text-gold mb-4" />
              <p className="text-3xl font-heading font-semibold text-navy mb-1">{invitations.length}</p>
              <p className="text-sm text-slate-700">Pending Invitations</p>
            </div>
          </div>
        )}

        <Tabs defaultValue="pending">
          <TabsList className="bg-white border flex-wrap">
            <TabsTrigger value="pending">Pending Approvals ({pendingUsers.length})</TabsTrigger>
            <TabsTrigger value="invitations">Hotel Invitations</TabsTrigger>
            <TabsTrigger value="hotels">5-Star Hotels ({hotels5Star.length})</TabsTrigger>
            <TabsTrigger value="testimonials">Testimonials ({testimonials.length})</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="bookings">Property Bookings</TabsTrigger>
            <TabsTrigger value="gift-orders" className="bg-gold/10 text-gold">🎁 Gift Orders ({giftOrders.length})</TabsTrigger>
            <TabsTrigger value="trainer-bookings">👨‍🏫 Trainer Bookings</TabsTrigger>
            <TabsTrigger value="payments" className="bg-green-50 text-green-700">💳 Payments</TabsTrigger>
            <TabsTrigger value="violations" onClick={() => navigate('/admin/violations')}>Business Protection</TabsTrigger>
          </TabsList>

          {/* Pending Approvals Tab */}
          <TabsContent value="pending">
            <div className="bg-white p-6 rounded-lg shadow-card border">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <h3 className="text-xl font-bold text-navy">User Registrations Pending Approval</h3>
                
                {/* Search and Filter Controls */}
                <div className="flex flex-wrap items-center gap-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input 
                      placeholder="Search by name, email, company..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 w-64"
                    />
                  </div>
                  <Select value={roleFilter} onValueChange={setRoleFilter}>
                    <SelectTrigger className="w-40">
                      <Filter className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Filter by role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Roles</SelectItem>
                      <SelectItem value="client">Clients</SelectItem>
                      <SelectItem value="vendor">Vendors</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Bulk Action Buttons */}
              {selectedUsers.length > 0 && (
                <div className="flex items-center gap-3 mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <span className="text-sm font-medium text-blue-800">
                    {selectedUsers.length} user(s) selected
                  </span>
                  <Button 
                    onClick={handleBulkApprove}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Approve All
                  </Button>
                  <Button 
                    onClick={openBulkRejectDialog}
                    size="sm"
                    variant="outline"
                    className="border-red-500 text-red-500 hover:bg-red-50"
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Reject All
                  </Button>
                  <Button 
                    onClick={() => setSelectedUsers([])}
                    size="sm"
                    variant="ghost"
                  >
                    Clear Selection
                  </Button>
                </div>
              )}

              {filteredPendingUsers.length === 0 ? (
                <p className="text-slate-800 text-center py-8">
                  {pendingUsers.length === 0 ? 'No pending user approvals' : 'No users match your search criteria'}
                </p>
              ) : (
                <div className="space-y-4">
                  {/* Select All Checkbox */}
                  <div className="flex items-center gap-2 pb-2 border-b">
                    <Checkbox 
                      id="selectAll"
                      checked={selectedUsers.length === filteredPendingUsers.length && filteredPendingUsers.length > 0}
                      onCheckedChange={toggleSelectAll}
                    />
                    <Label htmlFor="selectAll" className="text-sm font-medium cursor-pointer">
                      Select All ({filteredPendingUsers.length})
                    </Label>
                  </div>
                  
                  {filteredPendingUsers.map((pendingUser) => (
                    <div key={pendingUser.id} className={`border rounded-lg p-4 transition-colors ${
                      selectedUsers.includes(pendingUser.id) ? 'border-gold bg-gold/5' : 'border-gray-200 hover:border-gold'
                    }`}>
                      <div className="flex items-start gap-4">
                        {/* Checkbox */}
                        <Checkbox 
                          checked={selectedUsers.includes(pendingUser.id)}
                          onCheckedChange={() => toggleUserSelection(pendingUser.id)}
                          className="mt-1"
                        />
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="font-semibold text-navy">{pendingUser.name}</h4>
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                              pendingUser.role === 'vendor' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                            }`}>
                              {pendingUser.role.toUpperCase()}
                            </span>
                            <span className="px-3 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-700">
                              PENDING
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm text-slate-800">
                            <div>
                              <span className="font-medium">Email:</span> {pendingUser.email}
                            </div>
                            <div>
                              <span className="font-medium">Phone:</span> {pendingUser.phone || 'N/A'}
                            </div>
                            <div>
                              <span className="font-medium">Company:</span> {pendingUser.company || 'N/A'}
                            </div>
                            <div>
                              <span className="font-medium">Registered:</span> {new Date(pendingUser.created_at).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleApproveUser(pendingUser.id)}
                            className="bg-navy hover:bg-navy-700 text-white"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            onClick={() => openRejectDialog(pendingUser.id)}
                            variant="outline"
                            className="border-red-500 text-red-500 hover:bg-red-50"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Hotel Invitations Tab */}
          <TabsContent value="invitations" className="mt-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-heading text-xl text-navy">Hotel Invitations</h2>
              <div className="flex gap-3">
                <Button onClick={() => setShowBulkDialog(true)} variant="outline">
                  <Upload className="w-4 h-4 mr-2" /> Bulk Upload CSV
                </Button>
                <Button onClick={() => setShowInviteDialog(true)} className="bg-gold text-navy hover:bg-gold-light">
                  <Plus className="w-4 h-4 mr-2" /> Invite Hotel
                </Button>
              </div>
            </div>

            {invitations.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border">
                <Mail className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-slate-700">No invitations yet. Start inviting hotels to join the platform!</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg border overflow-hidden">
                <table className="w-full">
                  <thead className="bg-navy-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-800 uppercase">Hotel</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-800 uppercase">City</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-800 uppercase">Contact</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-800 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-800 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {invitations.map((inv) => (
                      <tr key={inv.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-navy">{inv.hotel_name}</td>
                        <td className="px-4 py-3 text-slate-800">{inv.hotel_city}</td>
                        <td className="px-4 py-3">
                          <p className="text-slate-900">{inv.contact_name}</p>
                          <p className="text-sm text-slate-700">{inv.contact_email}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            inv.status === 'used' ? 'bg-emerald-100 text-emerald-800' :
                            inv.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                            'bg-amber-100 text-amber-800'
                          }`}>
                            {inv.status.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <Button size="sm" variant="ghost" onClick={() => copyInviteLink(inv.invitation_code)}>
                              <Copy className="w-4 h-4" />
                            </Button>
                            {inv.status === 'pending' && (
                              <Button size="sm" variant="ghost" onClick={() => api.markInvitationSent(inv.id).then(fetchAdminData)}>
                                <Send className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* 5-Star Hotels Database Tab */}
          <TabsContent value="hotels" className="mt-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-heading text-xl text-navy">5-Star Hotels in India</h2>
              <div className="flex items-center gap-3">
                <Filter className="w-4 h-4 text-slate-700" />
                <Select value={selectedCity} onValueChange={setSelectedCity}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="All Cities" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Cities</SelectItem>
                    {hotelCities.map(city => (
                      <SelectItem key={city} value={city}>{city}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="bg-white rounded-lg border overflow-hidden">
              <table className="w-full">
                <thead className="bg-navy-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-800 uppercase">Hotel Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-800 uppercase">City</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-800 uppercase">State</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-800 uppercase">Chain</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-800 uppercase">Rooms</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-800 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredHotels.slice(0, 50).map((hotel, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Hotel className="w-4 h-4 text-gold" />
                          <span className="font-medium text-navy">{hotel.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-800">{hotel.city}</td>
                      <td className="px-4 py-3 text-slate-700">{hotel.state}</td>
                      <td className="px-4 py-3 text-slate-800">{hotel.chain}</td>
                      <td className="px-4 py-3 text-slate-800">{hotel.rooms}</td>
                      <td className="px-4 py-3">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setShowInviteDialog(true);
                            // Pre-fill will be handled in the dialog
                          }}
                        >
                          Invite
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredHotels.length > 50 && (
                <div className="p-4 text-center text-sm text-slate-700 border-t">
                  Showing 50 of {filteredHotels.length} hotels
                </div>
              )}
            </div>
          </TabsContent>

          {/* Testimonials Tab */}
          <TabsContent value="testimonials" className="mt-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-heading text-xl text-navy">Testimonial Management</h2>
            </div>

            {testimonials.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border">
                <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-slate-700">No testimonials submitted yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {testimonials.map((t) => (
                  <div key={t.id} className="bg-white rounded-lg border p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="flex items-center gap-1">
                            {[...Array(t.rating)].map((_, i) => (
                              <Star key={i} className="w-4 h-4 text-gold fill-gold" />
                            ))}
                          </div>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            t.status === 'approved' ? 'bg-emerald-100 text-emerald-800' :
                            t.status === 'rejected' ? 'bg-red-100 text-red-800' :
                            'bg-amber-100 text-amber-800'
                          }`}>
                            {t.status.toUpperCase()}
                          </span>
                        </div>
                        <p className="text-slate-900 mb-3">"{t.review}"</p>
                        <div className="flex items-center gap-4 text-sm text-slate-700">
                          <span><strong>{t.user_name}</strong></span>
                          <span>{t.user_designation}, {t.user_company}</span>
                          <span>{t.event_type}</span>
                          {t.property_name && <span>@ {t.property_name}</span>}
                        </div>
                      </div>
                      {t.status === 'pending' && (
                        <div className="flex gap-2 ml-4">
                          <Button 
                            size="sm" 
                            className="bg-navy hover:bg-navy-700 text-white"
                            onClick={() => handleTestimonialAction(t.id, 'approved')}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" /> Approve
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => handleTestimonialAction(t.id, 'rejected')}
                          >
                            <XCircle className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="mt-6">
            <div className="bg-white rounded-lg border overflow-hidden">
              <table className="w-full">
                <thead className="bg-navy-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-800 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-800 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-800 uppercase">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-800 uppercase">Company</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {users.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium text-navy">{u.name}</td>
                      <td className="px-6 py-4 text-slate-800">{u.email}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          u.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                          u.role === 'vendor' ? 'bg-blue-100 text-blue-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {u.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-slate-800">{u.company || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings" className="mt-6">
            {bookings.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border">
                <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-slate-700">No bookings yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {bookings.map((booking) => (
                  <div key={booking.id} className="bg-white p-6 rounded-lg border">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-heading text-xl font-medium text-navy mb-2">{booking.property_name}</h3>
                        <div className="grid grid-cols-3 gap-4 text-sm text-slate-800">
                          <div><span className="font-semibold">Client:</span> {booking.client_name}</div>
                          <div><span className="font-semibold">Event:</span> {booking.event_type}</div>
                          <div><span className="font-semibold">Guests:</span> {booking.guests}</div>
                          <div><span className="font-semibold">Amount:</span> ₹{booking.total_amount?.toLocaleString()}</div>
                          <div><span className="font-semibold">Status:</span> {booking.status}</div>
                          <div><span className="font-semibold">Payment:</span> {booking.payment_status}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Gift Orders Tab */}
          <TabsContent value="gift-orders" className="mt-6">
            <div className="bg-white p-6 rounded-lg shadow-card border">
              <h3 className="text-xl font-bold text-navy mb-6">🎁 Gift Orders ({giftOrders.length})</h3>
              {giftOrders.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No gift orders yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-3 text-sm font-semibold text-navy">Order ID</th>
                        <th className="text-left p-3 text-sm font-semibold text-navy">Client</th>
                        <th className="text-left p-3 text-sm font-semibold text-navy">Gift</th>
                        <th className="text-left p-3 text-sm font-semibold text-navy">Quantity</th>
                        <th className="text-left p-3 text-sm font-semibold text-navy">Amount</th>
                        <th className="text-left p-3 text-sm font-semibold text-navy">Status</th>
                        <th className="text-left p-3 text-sm font-semibold text-navy">Payment</th>
                        <th className="text-left p-3 text-sm font-semibold text-navy">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {giftOrders.map((order) => (
                        <tr key={order.id} className="border-b hover:bg-slate-50">
                          <td className="p-3 text-sm font-mono">{order.id?.slice(0, 8)}...</td>
                          <td className="p-3">
                            <div className="text-sm font-medium">{order.client_name || order.delivery_name}</div>
                            <div className="text-xs text-slate-500">{order.client_email || order.delivery_email}</div>
                          </td>
                          <td className="p-3 text-sm">{order.gift_name}</td>
                          <td className="p-3 text-sm">{order.quantity}</td>
                          <td className="p-3 text-sm font-semibold text-green-600">₹{order.total_amount?.toLocaleString()}</td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                              order.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                              order.status === 'shipped' ? 'bg-purple-100 text-purple-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {order.status}
                            </span>
                          </td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              order.payment_status === 'paid' || order.payment_status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {order.payment_status}
                            </span>
                          </td>
                          <td className="p-3 text-sm text-slate-500">
                            {order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Trainer Bookings Tab */}
          <TabsContent value="trainer-bookings" className="mt-6">
            <div className="bg-white p-6 rounded-lg shadow-card border">
              <h3 className="text-xl font-bold text-navy mb-6">👨‍🏫 Trainer Bookings ({trainerBookings.length})</h3>
              {trainerBookings.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No trainer bookings yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-3 text-sm font-semibold text-navy">Booking ID</th>
                        <th className="text-left p-3 text-sm font-semibold text-navy">Client</th>
                        <th className="text-left p-3 text-sm font-semibold text-navy">Trainer</th>
                        <th className="text-left p-3 text-sm font-semibold text-navy">Category</th>
                        <th className="text-left p-3 text-sm font-semibold text-navy">Amount</th>
                        <th className="text-left p-3 text-sm font-semibold text-navy">Status</th>
                        <th className="text-left p-3 text-sm font-semibold text-navy">Payment</th>
                        <th className="text-left p-3 text-sm font-semibold text-navy">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trainerBookings.map((booking) => (
                        <tr key={booking.id} className="border-b hover:bg-slate-50">
                          <td className="p-3 text-sm font-mono">{booking.id?.slice(0, 8)}...</td>
                          <td className="p-3">
                            <div className="text-sm font-medium">{booking.client_name}</div>
                            <div className="text-xs text-slate-500">{booking.client_email}</div>
                          </td>
                          <td className="p-3 text-sm">{booking.trainer_name}</td>
                          <td className="p-3 text-sm">{booking.category}</td>
                          <td className="p-3 text-sm font-semibold text-green-600">₹{booking.total_amount?.toLocaleString()}</td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                              booking.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {booking.status}
                            </span>
                          </td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              booking.payment_status === 'paid' || booking.payment_status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {booking.payment_status}
                            </span>
                          </td>
                          <td className="p-3 text-sm text-slate-500">
                            {booking.created_at ? new Date(booking.created_at).toLocaleDateString() : 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Payment Transactions Tab */}
          <TabsContent value="payments" className="mt-6">
            <div className="bg-white p-6 rounded-lg shadow-card border">
              <h3 className="text-xl font-bold text-navy mb-6">💳 Payment Transactions ({paymentTransactions.length})</h3>
              {paymentTransactions.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No payment transactions yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-3 text-sm font-semibold text-navy">Transaction ID</th>
                        <th className="text-left p-3 text-sm font-semibold text-navy">Razorpay Payment ID</th>
                        <th className="text-left p-3 text-sm font-semibold text-navy">Order Type</th>
                        <th className="text-left p-3 text-sm font-semibold text-navy">Amount</th>
                        <th className="text-left p-3 text-sm font-semibold text-navy">Status</th>
                        <th className="text-left p-3 text-sm font-semibold text-navy">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentTransactions.map((txn) => (
                        <tr key={txn.id} className="border-b hover:bg-slate-50">
                          <td className="p-3 text-sm font-mono">{txn.id?.slice(0, 8)}...</td>
                          <td className="p-3 text-sm font-mono text-blue-600">{txn.razorpay_payment_id || txn.payment_id || 'N/A'}</td>
                          <td className="p-3 text-sm">{txn.order_type || 'Gift Order'}</td>
                          <td className="p-3 text-sm font-semibold text-green-600">₹{txn.amount?.toLocaleString()}</td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              txn.status === 'completed' || txn.status === 'success' ? 'bg-green-100 text-green-700' :
                              txn.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {txn.status}
                            </span>
                          </td>
                          <td className="p-3 text-sm text-slate-500">
                            {txn.created_at ? new Date(txn.created_at).toLocaleDateString() : 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Invite Hotel Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-navy">Invite Hotel</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleInviteHotel} className="space-y-4">
            <div>
              <Label>Hotel Name</Label>
              <Input name="hotel_name" placeholder="Taj Palace Delhi" required />
            </div>
            <div>
              <Label>City</Label>
              <Select name="hotel_city" required>
                <SelectTrigger><SelectValue placeholder="Select city" /></SelectTrigger>
                <SelectContent>
                  {hotelCities.map(city => (
                    <SelectItem key={city} value={city}>{city}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Contact Person Name</Label>
              <Input name="contact_name" placeholder="Mr. Sharma" required />
            </div>
            <div>
              <Label>Contact Email</Label>
              <Input type="email" name="contact_email" placeholder="sales@hotel.com" required />
            </div>
            <div>
              <Label>Contact Phone (Optional)</Label>
              <Input name="contact_phone" placeholder="+91 98765 43210" />
            </div>
            <Button type="submit" className="w-full bg-gold text-navy hover:bg-gold-light">
              Generate Invitation Link
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-navy">Bulk Upload Hotels</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-navy-50 p-4 rounded-lg">
              <p className="text-sm text-slate-900 font-medium mb-2">CSV Format:</p>
              <code className="text-xs text-slate-800 block bg-white p-2 rounded">
                hotel_name,hotel_city,contact_name,contact_email,contact_phone
              </code>
            </div>
            
            <div>
              <input
                type="file"
                accept=".csv"
                ref={fileInputRef}
                onChange={handleFileUpload}
                className="hidden"
              />
              <Button 
                variant="outline" 
                onClick={() => fileInputRef.current?.click()}
                className="w-full"
              >
                <Upload className="w-4 h-4 mr-2" /> Upload CSV File
              </Button>
            </div>

            <div>
              <Label>Or paste CSV data:</Label>
              <Textarea
                value={csvData}
                onChange={(e) => setCsvData(e.target.value)}
                placeholder="hotel_name,hotel_city,contact_name,contact_email,contact_phone
Taj Palace,Delhi,Mr. Kumar,sales@taj.com,+919876543210
ITC Grand,Chennai,Ms. Sharma,events@itc.com,+919876543211"
                rows={8}
              />
            </div>

            <Button onClick={handleBulkUpload} className="w-full bg-gold text-navy hover:bg-gold-light" disabled={!csvData.trim()}>
              Process & Create Invitations
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject User Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              {isBulkReject ? `Reject ${selectedUsers.length} Users` : 'Reject User'}
            </DialogTitle>
            <DialogDescription>
              {isBulkReject 
                ? `You are about to reject ${selectedUsers.length} user(s). They will be notified via email.`
                : 'Please provide a reason for rejection. The user will be notified via email.'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="rejectReason">Rejection Reason</Label>
              <Select value={rejectReason} onValueChange={setRejectReason}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a reason..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Does not meet requirements">Does not meet requirements</SelectItem>
                  <SelectItem value="Incomplete information">Incomplete information</SelectItem>
                  <SelectItem value="Duplicate account">Duplicate account</SelectItem>
                  <SelectItem value="Invalid business credentials">Invalid business credentials</SelectItem>
                  <SelectItem value="Suspicious activity">Suspicious activity</SelectItem>
                  <SelectItem value="custom">Custom reason...</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {rejectReason === 'custom' && (
              <div>
                <Label htmlFor="customReason">Custom Reason</Label>
                <Textarea 
                  id="customReason"
                  placeholder="Enter your custom rejection reason..."
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={3}
                />
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={confirmReject}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <XCircle className="w-4 h-4 mr-1" />
              Confirm Rejection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
